import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { Fragment, useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useNavigate } from 'react-router'
import { ButtonPrimary, ButtonRedSecondary } from '../../../../../../../../../baseComponents/Button'
import { ComponentWithPermissions } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import SingleFormLayout from '../../../../../../../../../baseComponents/Layout/SingleFormLayout'
import TwinTrans from '../../../../../../../../../baseComponents/TwinTrans'
import { FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import ModalChangeActiveCustomer from '../../../../../../../../../specificComponents/Customers/ModalChangeActiveCustomerCustomer'
import { addDictionaryExtraLine, TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import locations from '../../../../../../../../../utils/globals/locations'
import { valueOrDefaultValue } from '../../../../../../../../../utils/globals/valueOrDefaultValue'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import useEditDeleteModal from '../../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { useTypeIdNumber } from '../../../../../../../../../utils/hooks/useTypeIdNumber'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../../../../../utils/reducers/company/employees'
import { getActiveLangs, getAllClientManagerEmployees, getConfigParam } from '../../../../../../../../../utils/reducers/getters'
import ModalContactB2CChangeType from './ModalContactB2CChangeType'


type MainContactB2CProfileProps = ComponentWithPermissions & ReduxMainContactB2CProfile & {
    basicData: CustomerModelType | null
    getData: () => Promise<void>
    extraProps?: TwinDictionary
}

interface CustomerModelTypeExtended extends CustomerModelType {
    transform_to_client: string
}

const MainContactB2CProfile: React.FC<MainContactB2CProfileProps> = ({ basicData, userPermissions, getData, extraProps, employees }) => {
    const { t } = useTwinTranslation()
    const { typesIdNumber } = useTypeIdNumber()
    const { openModal, setOpenModal } = useMainContactB2CProfileLogic()
    const myEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getConfigParam('showOnlyClientManagerEmployees') === 'on' ? getAllClientManagerEmployees() || {} : employees || {}))), [employees])
    const navigate = useNavigate()
    const showAllCustomersTogether = getConfigParam('showAllCustomersTogether') === 'on' ? true : false

    const fields: FormRender<CustomerModelTypeExtended> = [
        {
            cols: 2,
            title: t('generalInformation', 'Información general'),
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                {
                    name: 'surname',
                    label: t('surname', 'Apellido'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'type_id_number',
                    label: t('typeIdNumber', 'Tipo de documento identificación'),
                    component: 'CustomSelect',
                    items: typesIdNumber,
                    value: basicData?.type_id_number,
                },
                {
                    name: 'id_number',
                    label: t('id_number', 'DNI'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'code',
                    label: t('customerCode', 'Código de Cliente'),
                    component: 'InputWithLabelMarginIntegerStateFull',
                },
                {
                    name: 'customer_card',
                    label: t('customerCard', 'Tarjeta de cliente'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'responsable_name',
                    label: t('responsableName', 'Nombre del responsable'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'ResponsibleEmployeeId',
                    label: t('ourManager', 'Nuestro gestor'),
                    component: 'CustomSelectWithSearchBar',
                    items: myEmployees || {},
                    fieldName: 'fullname_short',
                    sortItems: true
                }
            ]
        },
        {
            cols: 2,
            title: t('contactInformation', 'Datos de contacto'),
            elements: [
                {
                    name: 'phone',
                    label: t('phone', 'Teléfono'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'mobile',
                    label: t('mobile', 'Móvil'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'email',
                    label: t('email', 'Email'),
                    component: 'InputWithLabelMargin',
                    type: 'email',
                },
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: getActiveLangs(),
                },
                {
                    name: 'CustomerOriginId',
                    label: t('origin', 'Origen'),
                    component: 'CustomSelectWithSearchBar',
                    items: extraProps?.dictCustomerOrigins || {},
                },
                {
                    name: 'newsletter',
                    label: t('newsletter', 'Newsletter'),
                    component: 'CheckboxMargin',
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: basicData?.id
                }
            ]
        },
    ]
    const parsedFields = valueOrDefaultValue(fields, userPermissions, basicData)

    const extraButtons = [
        <ButtonRedSecondary className='regular14' onClick={() => setOpenModal({ type: 'delete', allRowData: {} })}><TwinTrans transKey='delete'>Eliminar</TwinTrans></ButtonRedSecondary>,
        <ButtonPrimary key='transformToCustomer' className='regular14' onClick={() => setOpenModal({ type: 'create', allRowData: {} })}><TwinTrans transKey='saveAndTransformToCustomerB2C'>Guardar y Transformar a cliente B2C</TwinTrans></ButtonPrimary>,
    ]
    return (
        <Fragment>
            <SingleFormLayout action='/api/app/customer/updateCustomer' onSubmit={getData} sections={parsedFields} userPermissions={userPermissions} extraButtonsLeft={extraButtons}>
            </SingleFormLayout>
            {openModal?.type === 'create' ? <ModalContactB2CChangeType setOpened={() => setOpenModal(null)} onSubmit={getData} allRowData={basicData || {}} /> : null}
            {openModal?.type === 'delete' ? <ModalChangeActiveCustomer {...{ setOpened: () => setOpenModal(null) }} allRowData={basicData || {}} onSubmit={() => navigate(locations.customer + '/' + (showAllCustomersTogether ? 'allCustomers' : 'contacts'))} /> : null}
        </Fragment>
    )
}

const useMainContactB2CProfileLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return { openModal, setOpenModal }
}


const mainContactB2CProfileDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxMainContactB2CProfile = ConnectedProps<typeof mainContactB2CProfileConnect>
const mapMainContactB2CProfileTypesConnector = (state: AllReduxPayloads) => ({ employees: state.employees })
const mainContactB2CProfileConnect = connect(mapMainContactB2CProfileTypesConnector, mainContactB2CProfileDispatch)

const mainContactB2CProfileConnectLoading = withLoading(MainContactB2CProfile, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])

const ModalCreateMainContactB2CProfileConnect = mainContactB2CProfileConnect(mainContactB2CProfileConnectLoading)


export default ModalCreateMainContactB2CProfileConnect